import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';

const TestimonialCarousel = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  if (!testimonials || testimonials.length === 0) return null;

  return (
    <div className="relative">
      <div className="relative bg-white rounded-2xl shadow-xl p-8 md:p-12 overflow-hidden">
        {/* Quote Icon */}
        <Quote className="absolute top-8 right-8 w-16 h-16 text-emerald-100" />

        {/* Testimonial Content */}
        <div className="relative z-10">
          <div className="flex gap-1 mb-6">
            {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
              <Star key={`star-${i}`} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
            ))}
          </div>
          
          <p className="text-gray-700 text-lg md:text-xl leading-relaxed mb-8 italic">
            "{testimonials[currentIndex].text}"
          </p>

          <div>
            <h4 className="text-gray-900 font-semibold text-lg">
              {testimonials[currentIndex].name}
            </h4>
            <p className="text-emerald-600 font-medium">
              {testimonials[currentIndex].role}
            </p>
            <p className="text-gray-500 text-sm">
              {testimonials[currentIndex].company}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      {testimonials.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-700 hover:bg-emerald-600 hover:text-white transition-all duration-200 transform hover:scale-110"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-700 hover:bg-emerald-600 hover:text-white transition-all duration-200 transform hover:scale-110"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {testimonials.length > 1 && (
        <div className="flex justify-center gap-2 mt-8">
          {testimonials.map((t, index) => (
            <button
              key={t.id || t._id || t.name}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'w-8 bg-emerald-600' : 'w-2 bg-gray-300 hover:bg-emerald-400'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TestimonialCarousel;
